import React from "react";
import { useSelector } from "react-redux";
import { Container, Box, Typography, makeStyles } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DetailsTab from "src/components/hotel/DetailsTab";
import RoomsTab from "src/components/hotel/RoomsTab";
import ImagesTab from "src/components/hotel/ImagesTab";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const STEPS = [
  { key: "details", label: "Property Details", number: 1 },
  { key: "rooms", label: "Rooms", number: 2 },
  { key: "images", label: "Images", number: 3 },
];

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: TOKENS.surface,
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(8),
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: TOKENS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 14,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  stepper: {
    display: "flex",
    alignItems: "center",
    marginBottom: 28,
    padding: "16px 20px",
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 14,
  },
  step: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: 1,
    padding: "6px 0",
  },
  stepCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepCircleActive: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
  },
  stepCircleDone: {
    backgroundColor: "#ECFDF5",
    color: "#059669",
  },
  stepCircleUpcoming: {
    backgroundColor: TOKENS.divider,
    color: TOKENS.textMuted,
  },
  stepLabel: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  stepLabelActive: { color: TOKENS.textPrimary },
  stepLabelDone: { color: "#059669" },
  stepLabelUpcoming: { color: TOKENS.textMuted },
  stepConnector: {
    flex: "0 0 40px",
    height: 2,
    backgroundColor: TOKENS.border,
    margin: "0 8px",
  },
  stepConnectorDone: {
    backgroundColor: "#A7F3D0",
  },
}));

const stepIndex = (key) => STEPS.findIndex((s) => s.key === key);

function CreateHotel() {
  const classes = useStyles();
  const tabValue = useSelector((state) => state.homes.add_hotel_tab);
  const activeIdx = stepIndex(tabValue);

  return (
    <Container className={classes.root}>
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          List a New Hotel Property
        </Typography>
        <Typography className={classes.pageSubtitle}>
          Hotels, B&Bs, guest houses, hostels, and similar accommodations
        </Typography>
      </Box>

      {/* Stepper */}
      <Box className={classes.stepper}>
        {STEPS.map((step, idx) => {
          const isDone = idx < activeIdx;
          const isActive = idx === activeIdx;
          return (
            <React.Fragment key={step.key}>
              <Box className={classes.step}>
                <Box
                  className={`${classes.stepCircle} ${
                    isDone
                      ? classes.stepCircleDone
                      : isActive
                        ? classes.stepCircleActive
                        : classes.stepCircleUpcoming
                  }`}
                >
                  {isDone ? (
                    <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                  ) : (
                    step.number
                  )}
                </Box>
                <Typography
                  className={`${classes.stepLabel} ${
                    isDone
                      ? classes.stepLabelDone
                      : isActive
                        ? classes.stepLabelActive
                        : classes.stepLabelUpcoming
                  }`}
                >
                  {step.label}
                </Typography>
              </Box>
              {idx < STEPS.length - 1 && (
                <Box
                  className={`${classes.stepConnector} ${
                    isDone ? classes.stepConnectorDone : ""
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {tabValue === "details" && <DetailsTab />}
      {tabValue === "rooms" && <RoomsTab />}
      {tabValue === "images" && <ImagesTab />}
    </Container>
  );
}

export default CreateHotel;
